import React, { FunctionComponent, ReactNode, useContext, useEffect } from 'react'
import { ShippingOptionStatus, ShippingOptionErrorType } from '../../model'
import { useShippingOption, useAllowedShippingOptions, useShippingAddress, useIsPickup } from '../../state'

import { ShippingOptionWrapper } from './ShippingOptionWrapper'
import { ShippingOptionList } from './ShippingOptionList'
import { ShippingOptionError } from '../ShippingOptionError'
import { isNullOrUndefined, isUndefinedOrEmpty } from '@afterpay/utils'
import { isValidAddress } from '../../utils/profile-validation'
import { useChangeShippingOption, useRetrieveShippingOptions, useShippingOptionError } from './hooks'
import { ShippingOptionContext } from './context'
import { ShippingOptionLoader } from './ShippingOptionLoader'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { ShippingOptionWarningMessage } from './ShippingOptionWarningMessage'
import styles from './style.module.css'

export const ShippingOptionSection: FunctionComponent = () => {
  const isPickup = useIsPickup()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const retrieveShippingOptions = useRetrieveShippingOptions()
  const handleShippingOptionChange = useChangeShippingOption()
  const shippingOptionErrorMessage = useShippingOptionError()
  const { setIsCollapse, setIsExpanded, showWarningMessage } = useContext(ShippingOptionContext)

  const { shippingOptionsStatus, selectedShippingOption, error: shippingOptionError } = useShippingOption()

  const { chosenAddress, isShippingAddressChanged, pickupAddress } = useShippingAddress()
  const allowedShippingOptions = useAllowedShippingOptions()

  const isShippingOptionUnableToUpdate = shippingOptionError === ShippingOptionErrorType.ShippingOptionUnableToUpdate

  const isShippingOptionLoading = shippingOptionsStatus <= ShippingOptionStatus.Loading
  const isShippingOptionLoaded =
    !isShippingOptionLoading && (shippingOptionsStatus !== ShippingOptionStatus.Error || isShippingOptionUnableToUpdate) && allowedShippingOptions.length > 0
  const showUnableToUpdateError = shippingOptionError === ShippingOptionErrorType.ShippingOptionUnableToUpdate && allowedShippingOptions.length === 1

  const hasAllowedShippingOptions = !(allowedShippingOptions.length === 0 && !isShippingOptionLoading)

  const renderShippingOptionError = (): ReactNode => {
    if (!isNullOrUndefined(shippingOptionErrorMessage)) {
      return (
        <ShippingOptionError
          error={shippingOptionErrorMessage.error}
          {...(shippingOptionErrorMessage.handleRetryButton && { handleRetryButton: handleShippingOptionChange })}
        />
      )
    }
    return null
  }

  useEffect(() => {
    const isAddressIncomplete = isUndefinedOrEmpty(chosenAddress?.phoneNumber) || !isValidAddress(chosenAddress)
    if (!isAddressIncomplete || isPickup) {
      if (isShippingAddressChanged || isShippingOptionLoading) {
        if (!isArcadeDesignLanguage) {
          setIsCollapse(false)
        } else {
          setIsExpanded(true)
        }
        retrieveShippingOptions()
      }
    }
  }, [chosenAddress, pickupAddress])

  if (isArcadeDesignLanguage) {
    return (
      <>
        <ShippingOptionWrapper selectedShippingOption={selectedShippingOption} hasAllowedShippingOptions={hasAllowedShippingOptions}>
          {showWarningMessage && (
            <div className={styles.inlineWarningMessageContainer} data-testid='shipping-option-section-inline-warning-message-container'>
              <ShippingOptionWarningMessage />
            </div>
          )}
          {isShippingOptionLoaded && !showUnableToUpdateError && <ShippingOptionList />}
          {isShippingOptionLoading && <ShippingOptionLoader />}
        </ShippingOptionWrapper>

        {renderShippingOptionError()}
      </>
    )
  }

  return (
    <ShippingOptionWrapper selectedShippingOption={selectedShippingOption} hasAllowedShippingOptions={hasAllowedShippingOptions}>
      {isShippingOptionLoaded && !showUnableToUpdateError && <ShippingOptionList />}
      {renderShippingOptionError()}
      {isShippingOptionLoading && <ShippingOptionLoader />}
    </ShippingOptionWrapper>
  )
}
